import { useCallback, useEffect, useState } from "react";
import {
	Text,
	Spinner,
	Alert,
	AlertIcon,
	useDisclosure,
} from "@chakra-ui/react";
import { FiEdit2, FiPlus, FiSave, FiTrash2 } from "react-icons/fi";

import { Button } from "newui/components/Button";
import { showToast } from "utils/helpers";
import { Modal } from "newui/components/ui/Modal";
import type {
	ScheduleColumn,
	ScheduleOfRatesData,
	ScheduleRow,
} from "./schedule-of-rates.types";
import {
	getCompanyScheduleOfRates,
	saveCompanyScheduleOfRates,
} from "../../api";
import { useScheduleEditor } from "./useScheduleEditor";
import { ScheduleTableView } from "./components/ScheduleOfRatesTableView";
import { EditScheduleModal } from "./components/EditScheduleOfRatesModal";

export interface CompanyScheduleSettingsProps {
	companyId: number;
}

const CompanyScheduleSettings: React.FC<CompanyScheduleSettingsProps> = ({
	companyId,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [saveSuccess, setSaveSuccess] = useState(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const {
		isOpen: isRowDeleteOpen,
		onOpen: onRowDeleteOpen,
		onClose: onRowDeleteClose,
	} = useDisclosure();
	const [pendingRowDeleteId, setPendingRowDeleteId] = useState<number | null>(null);

	const editor = useScheduleEditor({
		initialColumns: [],
		initialRows: [],
	});

	const fetchSchedule = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await getCompanyScheduleOfRates(companyId);
			if (data) {
				editor.reset(data.columns, data.rows);
			}
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsLoading(false);
		}
	}, [companyId, editor.reset]);

	useEffect(() => {
		fetchSchedule();
	}, [fetchSchedule]);

	const handleSave = useCallback(async () => {
		const cols = editor.getColumns();
		const rowData = editor.getRows();

		if (rowData.length > 0) {
			const errors = new Set<string>();
			rowData.forEach((row) => {
				cols.forEach((col) => {
					const value = row.values[col.id] ?? "";
					if (!value.trim()) {
						errors.add(`${row.id}-${col.id}`);
					}
				});
			});
			if (errors.size > 0) {
				showToast("Please fill in all fields before saving", "Error", false);
				return;
			}
		}

		setIsSaving(true);
		setSaveSuccess(false);
		try {
			await saveCompanyScheduleOfRates(companyId, {
				name: "Company Schedule of Rates",
				columns: cols.map((c) => ({
					name: c.label,
					type: c.columnType,
					sort_order: c.sortOrder,
				})),
				rows: rowData.map((r) => ({
					sort_order: r.sortOrder,
					cells: r.values,
				})),
			});
			setSaveSuccess(true);
			showToast("Schedule of rates saved successfully", "Success", true);
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsSaving(false);
		}
	}, [companyId, editor.getColumns, editor.getRows]);

	const handleColumnsSave = useCallback(
		(newColumns: ScheduleColumn[]) => {
			const oldColumns = editor.getColumns();
			const currentRows = editor.getRows();

			const oldLabelToId: Record<string, number> = {};
			oldColumns.forEach((col) => {
				oldLabelToId[`${col.label}-${col.columnType}`] = col.id;
			});

			const remappedRows: ScheduleRow[] = currentRows.map((row) => {
				const newValues: Record<number, string> = {};
				newColumns.forEach((newCol) => {
					const oldId = oldLabelToId[`${newCol.label}-${newCol.columnType}`];
					if (oldId !== undefined && row.values[oldId] !== undefined) {
						newValues[newCol.id] = row.values[oldId];
					}
				});
				return { ...row, values: newValues };
			});

			editor.reset(newColumns, remappedRows);
			setIsEditModalOpen(false);
		},
		[editor.getColumns, editor.getRows, editor.reset]
	);

	const confirmRemoveRow = useCallback(
		(rowId: number) => {
			setPendingRowDeleteId(rowId);
			onRowDeleteOpen();
		},
		[onRowDeleteOpen]
	);

	const handleConfirmRowDelete = useCallback(() => {
		if (pendingRowDeleteId !== null) {
			editor.removeRow(pendingRowDeleteId);
		}
		setPendingRowDeleteId(null);
		onRowDeleteClose();
	}, [pendingRowDeleteId, editor.removeRow, onRowDeleteClose]);

	if (isLoading) {
		return (
			<div
				className="flex items-center justify-center py-12"
				data-testid="company-schedule-loading"
			>
				<Spinner size="lg" />
			</div>
		);
	}

	const hasContent = editor.columns.length > 0;

	return (
		<div data-testid="company-schedule-settings">
			{/* Header */}
			<div className="mb-4 flex items-center justify-between">
				<div>
					<Text className="font-roboto text-lg font-semibold text-gray-900">
						Schedule of Rates
					</Text>
					<Text className="text-sm text-gray-500">
						Define your company&apos;s default schedule of rates template. This
						will be used as the starting point when adding a schedule to new
						submissions.
					</Text>
				</div>
				<div className="flex items-center gap-2">
					<Button
						size="sm"
						btntype="secondary"
						onClick={() => setIsEditModalOpen(true)}
						data-testid="edit-company-schedule-btn"
					>
						<FiEdit2 size={14} className="mr-1" />
						Edit Columns
					</Button>
					{hasContent && (
						<Button
							size="sm"
							btntype="primary"
							onClick={handleSave}
							disabled={isSaving}
							data-testid="save-company-schedule-btn"
						>
							<FiSave className="mr-1" />
							{isSaving ? "Saving..." : "Save Template"}
						</Button>
					)}
				</div>
			</div>

			{saveSuccess && (
				<Alert
					status="success"
					className="mb-4 rounded-md"
					data-testid="save-success-alert"
				>
					<AlertIcon />
					<Text className="text-sm">
						Schedule of rates template saved successfully.
					</Text>
				</Alert>
			)}

			{/* Table view */}
			{hasContent ? (
				<ScheduleTableView
					columns={editor.getColumns()}
					onAddRow={editor.addRow}
					onCellChange={editor.updateCellValue}
					onCurrencyBlur={editor.formatCurrencyCell}
					onRemoveRow={confirmRemoveRow}
					rows={editor.rows}
					validationErrors={editor.validationErrors}
				/>
			) : (
				<div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-[#D0D5DD] bg-white p-8">
					<Text className="text-sm text-gray-400">
						No columns configured yet. Click &ldquo;Edit Columns&rdquo; to set up your schedule structure.
					</Text>
				</div>
			)}

			{/* Edit columns modal */}
			<EditScheduleModal
				initialColumns={editor.getColumns()}
				isOpen={isEditModalOpen}
				onClose={() => setIsEditModalOpen(false)}
				onSave={handleColumnsSave}
				title="Edit Schedule Template Columns"
			/>

			{/* Delete row confirmation */}
			<Modal
				isOpen={isRowDeleteOpen}
				onClose={onRowDeleteClose}
				title="Remove Row"
				closeOnEsc
				closeOnOverlayClick
				primaryButton={{
					text: "Delete",
					onClick: handleConfirmRowDelete,
				}}
				secondaryButton={{
					onClick: onRowDeleteClose,
					text: "Cancel",
				}}
				iconType="error"
			>
				<Text>
					Are you sure you want to remove this row?
				</Text>
				<Text>
					This action cannot be undone.
				</Text>
			</Modal>
		</div>
	);
};

export { CompanyScheduleSettings };
